import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Search, Filter, Grid, List, SlidersHorizontal, Package } from 'lucide-react';
import { useTrucks } from '../hooks/useTrucks';
import { LoadingCard, LoadingPage } from '../components/LoadingSpinner';

const RigidsPage = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedMake, setSelectedMake] = useState('');
  const [selectedYear, setSelectedYear] = useState('');
  const [priceRange, setPriceRange] = useState('');
  const [viewMode, setViewMode] = useState('grid');
  const [showFilters, setShowFilters] = useState(false);

  // Filter for rigids only
  const apiFilters = useMemo(() => {
    const filters = {};
    if (searchTerm) filters.search = searchTerm;
    if (selectedMake) filters.make = selectedMake;
    if (selectedYear) filters.year = parseInt(selectedYear);
    return filters;
  }, [searchTerm, selectedMake, selectedYear]);

  const { trucks: allTrucks, loading, error } = useTrucks(apiFilters);

  // Filter for rigid vehicles and apply price range
  const rigids = useMemo(() => {
    let filtered = allTrucks.filter(truck => 
      truck.bodytype && truck.bodytype.toLowerCase().includes('rigid')
    );

    if (priceRange) {
      filtered = filtered.filter(truck => {
        const price = truck.price || 0;
        switch (priceRange) {
          case 'under25k': return price < 25000;
          case '25k-35k': return price >= 25000 && price <= 35000;
          case '35k-45k': return price > 35000 && price <= 45000;
          case 'over45k': return price > 45000;
          default: return true;
        }
      });
    }

    return filtered;
  }, [allTrucks, priceRange]);

  // Get unique values for filters
  const makes = [...new Set(rigids.map(truck => truck.make))].sort();
  const years = [...new Set(rigids.map(truck => truck.year))].sort((a, b) => b - a);

  const TruckCard = ({ truck, isListView = false }) => (
    <div className={`bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-all group ${
      isListView ? 'flex' : ''
    }`}>
      <div className={`relative ${isListView ? 'w-1/3' : ''}`}>
        <img 
          src={truck.images[0]} 
          alt={truck.title}
          className={`object-cover group-hover:scale-105 transition-transform duration-300 ${
            isListView ? 'w-full h-full' : 'w-full h-48'
          }`}
        />
        {truck.available_for_hire && (
          <div className="absolute top-4 left-4">
            <div className="bg-green-600 text-white px-3 py-1 rounded text-sm font-bold shadow-lg">
              Available for Hire
            </div>
          </div>
        )}
      </div>
      <div className={`p-6 ${isListView ? 'flex-1' : ''}`}>
        <h3 className="text-xl font-bold text-black mb-2 font-inter line-clamp-2">{truck.title}</h3>
        <div className="flex items-center gap-4 text-sm text-gray-600 mb-3">
          <span>{truck.year}</span>
          <span>•</span>
          <span>{truck.weight}t</span>
          <span>•</span>
          <span>{truck.power}</span>
        </div>
        <p className="text-gray-600 mb-4 line-clamp-2 font-inter">{truck.description}</p>
        
        {isListView && (
          <div className="grid grid-cols-2 gap-4 mb-4 text-sm">
            <div><strong>Mileage:</strong> {truck.mileage?.toLocaleString()} km</div>
            <div><strong>Emissions:</strong> {truck.emissions}</div>
            <div><strong>Body Type:</strong> {truck.bodytype}</div>
            <div><strong>Axle:</strong> {truck.axle}</div>
          </div>
        )}
        
        <div className="flex justify-between items-center">
          <div className="text-2xl font-bold text-black">
            {truck.price ? `£${truck.price.toLocaleString()}` : 'POA'}
          </div>
          <Link 
            to={`/trucks/${truck.id}`}
            className="bg-orange-600 hover:bg-orange-700 text-white px-4 py-2 rounded font-bold transition-colors"
          >
            View Details
          </Link>
        </div>
      </div>
    </div>
  );

  if (loading && rigids.length === 0) {
    return <LoadingPage message="Loading rigid vehicles..." />;
  }

  return (
    <div className="bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="bg-black text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="flex justify-center mb-4">
              <div className="bg-orange-600 rounded-full p-4">
                <Package className="h-12 w-12 text-white" />
              </div>
            </div>
            <h1 className="text-4xl lg:text-5xl font-black font-inter mb-4">
              Rigid <span className="text-orange-500">Vehicles</span>
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto font-inter">
              Versatile rigid vehicles ranging from 7.5 to 26 ton with various power ratings and different body types for all your business needs.
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Search and Filters */}
        <div className="bg-white rounded-lg shadow-lg p-6 mb-8">
          <div className="mb-4">
            <label className="block text-sm font-bold text-gray-700 mb-2 font-inter">Search MAN Rigid Vehicles</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
              <input
                type="text"
                placeholder="Search by make, model, tonnage, or specifications..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent font-inter"
              />
            </div>
          </div>

          <div className="flex justify-between items-center mb-4">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center gap-2 bg-gray-100 hover:bg-gray-200 px-4 py-2 rounded-lg transition-colors"
            >
              <SlidersHorizontal className="h-4 w-4" />
              Filters
            </button>
            
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600 font-inter">View:</span>
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 rounded ${viewMode === 'grid' ? 'bg-orange-600 text-white' : 'bg-gray-100 text-gray-600'}`}
              >
                <Grid className="h-4 w-4" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 rounded ${viewMode === 'list' ? 'bg-orange-600 text-white' : 'bg-gray-100 text-gray-600'}`}
              >
                <List className="h-4 w-4" />
              </button>
            </div>
          </div>

          {showFilters && (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 pt-4 border-t border-gray-200">
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2 font-inter">Make</label>
                <select
                  value={selectedMake}
                  onChange={(e) => setSelectedMake(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent font-inter"
                >
                  <option value="">All Makes</option>
                  {makes.map(make => (
                    <option key={make} value={make}>{make}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2 font-inter">Year</label>
                <select
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent font-inter"
                >
                  <option value="">All Years</option>
                  {years.map(year => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2 font-inter">Price Range</label>
                <select
                  value={priceRange}
                  onChange={(e) => setPriceRange(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent font-inter"
                >
                  <option value="">All Prices</option>
                  <option value="under25k">Under £25,000</option>
                  <option value="25k-35k">£25,000 - £35,000</option>
                  <option value="35k-45k">£35,000 - £45,000</option>
                  <option value="over45k">Over £45,000</option>
                </select>
              </div>
              
              <div className="flex items-end">
                <button
                  onClick={() => {
                    setSearchTerm('');
                    setSelectedMake('');
                    setSelectedYear('');
                    setPriceRange('');
                  }}
                  className="w-full bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg font-bold transition-colors"
                >
                  Clear Filters
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Results */}
        <div className="mb-6">
          <p className="text-gray-600 font-inter">
            Showing {rigids.length} rigid vehicle{rigids.length !== 1 ? 's' : ''}
            {loading && ' (loading...)'}
          </p>
          {error && (
            <p className="text-red-600 font-inter mt-2">{error}</p>
          )}
        </div>

        {/* Rigids Grid/List */}
        <div className={
          viewMode === 'grid' 
            ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8'
            : 'space-y-6'
        }>
          {loading && rigids.length === 0 ? (
            Array.from({ length: 6 }).map((_, index) => (
              <LoadingCard key={index} />
            ))
          ) : (
            rigids.map((truck) => (
              <TruckCard key={truck.id} truck={truck} isListView={viewMode === 'list'} />
            ))
          )}
        </div>

        {!loading && rigids.length === 0 && (
          <div className="text-center py-12">
            <div className="text-gray-400 text-6xl mb-4">📦</div>
            <h3 className="text-xl font-bold text-gray-700 mb-2 font-inter">No rigid vehicles found</h3>
            <p className="text-gray-600 font-inter">We currently don't have rigid vehicles matching your criteria. <Link to="/contact" className="text-orange-600 hover:underline">Contact us</Link> for specific requirements.</p>
          </div>
        )}

        {/* Why Choose Our Rigids */}
        <div className="mt-16 bg-white rounded-lg shadow-lg p-8">
          <h2 className="text-3xl font-black text-black mb-6 font-inter text-center">
            Perfect for <span className="text-orange-600">Every Business</span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="text-center">
              <div className="bg-orange-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <div className="text-orange-600 text-lg font-bold">7.5t</div>
              </div>
              <h3 className="font-bold text-black mb-2 font-inter">Light Rigids</h3>
              <p className="text-gray-600 font-inter">Perfect for urban deliveries and smaller loads.</p>
            </div>
            <div className="text-center">
              <div className="bg-orange-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <div className="text-orange-600 text-lg font-bold">18t</div>
              </div>
              <h3 className="font-bold text-black mb-2 font-inter">Medium Rigids</h3>
              <p className="text-gray-600 font-inter">Versatile vehicles for regional distribution.</p>
            </div>
            <div className="text-center">
              <div className="bg-orange-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <div className="text-orange-600 text-lg font-bold">26t</div>
              </div>
              <h3 className="font-bold text-black mb-2 font-inter">Heavy Rigids</h3>
              <p className="text-gray-600 font-inter">Maximum payload for demanding operations.</p>
            </div>
            <div className="text-center">
              <div className="bg-orange-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <div className="text-orange-600 text-2xl font-bold">✓</div>
              </div>
              <h3 className="font-bold text-black mb-2 font-inter">All Inspected</h3>
              <p className="text-gray-600 font-inter">Thoroughly checked and ready for work.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RigidsPage;